/**
 * Osano ANALYTICS consent bridge.
 *
 * Osano runs an opt-out model, so everything here FAILS OPEN: an absent or not-yet-initialised CMP
 * means granted. That is the opposite of the osano-consent.js helper this replaces.
 *
 * Only for consumers Osano cannot block by URL. Do not gate anything Osano already handles.
 */

const CONSENT_EVENTS = ['osano-cm-consent-saved', 'osano-cm-consent-changed'];

interface OsanoConsentManager {
	getConsent?: () => Record<string, string> | undefined;
	addEventListener?: (event: string, callback: (consent: Record<string, string>) => void) => void;
}

function osanoConsentManager(): OsanoConsentManager | undefined {
	return (window as any).Osano?.cm;
}

/**
 * True unless Osano explicitly reports ANALYTICS as declined. No polling: under an opt-out model
 * the only state worth waiting for is a decline, and Osano persists that before the page reloads.
 */
export function isAnalyticsConsented(): boolean {
	return osanoConsentManager()?.getConsent?.()?.['ANALYTICS'] !== 'DENY';
}

/**
 * Invoke `callback` whenever the user's ANALYTICS decision changes. Not called for the initial
 * state — read that with isAnalyticsConsented().
 *
 * Registration is single-shot by design. `Osano.cm` is assigned synchronously from inside osano.js,
 * which is parser-blocking and first in <head>, so by the time Angular boots it is either fully
 * built or Osano never ran at all — there is no window in which it arrives late. A missing `cm`
 * therefore means no CMP on this host and no decision to honour; index.html reports the case where
 * one should have loaded and did not.
 */
export function onAnalyticsConsentChange(callback: (granted: boolean) => void): void {
	const cm = osanoConsentManager();
	if (!cm?.addEventListener) {
		return;
	}

	// The event payload is deliberately ignored. Osano's payload shape differs between the two
	// events, and re-reading getConsent() keeps one source of truth shared with the initial read.
	CONSENT_EVENTS.forEach(event => {
		cm.addEventListener(event, () => callback(isAnalyticsConsented()));
	});
}
